import { allGalleryImages, albums as defaultAlbums, videoStories as defaultVideos, testimonials as defaultTestimonials } from "../data/mockData";

const KEYS = {
  bookings: "admin_bookings",
  images: "admin_images",
  albums: "admin_albums",
  deletedAlbums: "admin_deleted_album_ids",
  deletedAlbumTitles: "admin_deleted_album_titles",
  videos: "admin_videos",
  deletedVideos: "admin_deleted_video_ids",
  deletedVideoTitles: "admin_deleted_video_titles",
  testimonials: "admin_testimonials",
  homepage: "admin_homepage_settings"
};

const parseJson = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (e) {
    return fallback;
  }
};

export const getLocalImages = () => parseJson(localStorage.getItem(KEYS.images), []);
export const getLocalAlbums = () => parseJson(localStorage.getItem(KEYS.albums), []);
export const getLocalVideos = () => parseJson(localStorage.getItem(KEYS.videos), []);
export const getLocalBookings = () => parseJson(localStorage.getItem(KEYS.bookings), []);
export const getLocalHomepageSettings = () =>
  parseJson(localStorage.getItem(KEYS.homepage), { heroSliderImages: [] });
export const getDeletedAlbumIds = () => parseJson(localStorage.getItem(KEYS.deletedAlbums), []);
export const getDeletedAlbumTitles = () => parseJson(localStorage.getItem(KEYS.deletedAlbumTitles), []);
export const getDeletedVideoIds = () => parseJson(localStorage.getItem(KEYS.deletedVideos), []);
export const getDeletedVideoTitles = () => parseJson(localStorage.getItem(KEYS.deletedVideoTitles), []);
export const getLocalTestimonials = () => parseJson(localStorage.getItem(KEYS.testimonials), []);

export const setLocalImages = (images) => localStorage.setItem(KEYS.images, JSON.stringify(images));
export const setLocalAlbums = (albums) => localStorage.setItem(KEYS.albums, JSON.stringify(albums));
export const setLocalVideos = (videos) => localStorage.setItem(KEYS.videos, JSON.stringify(videos));
export const setLocalBookings = (bookings) => localStorage.setItem(KEYS.bookings, JSON.stringify(bookings));
export const setLocalHomepageSettings = (settings) =>
  localStorage.setItem(KEYS.homepage, JSON.stringify(settings));
export const setDeletedAlbumIds = (ids) =>
  localStorage.setItem(KEYS.deletedAlbums, JSON.stringify(ids));
export const setDeletedAlbumTitles = (titles) =>
  localStorage.setItem(KEYS.deletedAlbumTitles, JSON.stringify(titles));
export const setDeletedVideoIds = (ids) =>
  localStorage.setItem(KEYS.deletedVideos, JSON.stringify(ids));
export const setDeletedVideoTitles = (titles) =>
  localStorage.setItem(KEYS.deletedVideoTitles, JSON.stringify(titles));
export const setLocalTestimonials = (testimonials) =>
  localStorage.setItem(KEYS.testimonials, JSON.stringify(testimonials));

export const seedLocalDefaultsIfEmpty = () => {
  const bookings = getLocalBookings();
  const testimonials = getLocalTestimonials();

  if (!bookings.length) setLocalBookings([]);
  if (!testimonials.length) setLocalTestimonials(defaultTestimonials);
};

export const addLocalImage = (image) => {
  const images = getLocalImages();
  const next = [...images, image];
  setLocalImages(next);
  return next;
};

export const addLocalAlbum = (album) => {
  const albums = getLocalAlbums();
  const next = [album, ...albums];
  setLocalAlbums(next);
  return next;
};

export const deleteLocalAlbum = (albumId) => {
  const albums = getLocalAlbums();
  const next = albums.filter((album) => album.id !== albumId);
  setLocalAlbums(next);
  return next;
};

export const markAlbumDeletedLocally = (albumId) => {
  const deletedIds = getDeletedAlbumIds();
  if (deletedIds.includes(albumId)) return deletedIds;
  const next = [...deletedIds, albumId];
  setDeletedAlbumIds(next);
  return next;
};

export const clearDeletedAlbumMark = (albumId) => {
  const deletedIds = getDeletedAlbumIds();
  const next = deletedIds.filter((id) => id !== albumId);
  setDeletedAlbumIds(next);
  return next;
};

export const markAlbumTitleDeletedLocally = (albumTitle) => {
  const title = String(albumTitle || '').trim();
  if (!title) return getDeletedAlbumTitles();
  const deletedTitles = getDeletedAlbumTitles();
  if (deletedTitles.includes(title)) return deletedTitles;
  const next = [...deletedTitles, title];
  setDeletedAlbumTitles(next);
  return next;
};

export const updateLocalImage = (imageId, payload) => {
  const images = getLocalImages();
  const next = images.map((img) => (img.id === imageId ? { ...img, ...payload } : img));
  setLocalImages(next);
  return next;
};

export const deleteLocalImage = (imageId) => {
  const images = getLocalImages();
  const next = images.filter((img) => img.id !== imageId);
  setLocalImages(next);
  return next;
};

export const addLocalBooking = (booking) => {
  const bookings = getLocalBookings();
  const next = [booking, ...bookings];
  setLocalBookings(next);
  return next;
};

export const addLocalVideo = (video) => {
  const videos = getLocalVideos();
  const next = [video, ...videos];
  setLocalVideos(next);
  return next;
};

export const deleteLocalVideo = (videoId) => {
  const videos = getLocalVideos();
  const next = videos.filter((video) => video.id !== videoId);
  setLocalVideos(next);
  return next;
};

export const markVideoDeletedLocally = (videoId) => {
  const deletedIds = getDeletedVideoIds();
  if (deletedIds.includes(videoId)) return deletedIds;
  const next = [...deletedIds, videoId];
  setDeletedVideoIds(next);
  return next;
};

export const markVideoTitleDeletedLocally = (videoTitle) => {
  const title = String(videoTitle || '').trim();
  if (!title) return getDeletedVideoTitles();
  const deletedTitles = getDeletedVideoTitles();
  if (deletedTitles.includes(title)) return deletedTitles;
  const next = [...deletedTitles, title];
  setDeletedVideoTitles(next);
  return next;
};

export const addLocalTestimonial = (testimonial) => {
  const testimonials = getLocalTestimonials();
  const next = [testimonial, ...testimonials];
  setLocalTestimonials(next);
  return next;
};

export const updateLocalTestimonial = (testimonialId, payload) => {
  const testimonials = getLocalTestimonials();
  const next = testimonials.map((item) => (item.id === testimonialId ? { ...item, ...payload } : item));
  setLocalTestimonials(next);
  return next;
};

export const deleteLocalTestimonial = (testimonialId) => {
  const testimonials = getLocalTestimonials();
  const next = testimonials.filter((item) => item.id !== testimonialId);
  setLocalTestimonials(next);
  return next;
};
